import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuthentication, useChannels, useFlightRooms } from '@connect-x-app/hybrid-state';
import { hasOpenTasks, useInternetConnection } from '@connect-x-app/shared-utils';
import classNames from 'classnames';

import { ChannelSidebar, ChannelTabs, ChatHeader, FlightRoomInfoSideModal } from './components';

import './ChannelLayout.scss';

export const ChannelLayout = () => {
  const { closeFlightRoom, openFlightRoom, selectedFlightRoom } = useFlightRooms();
  const { user } = useAuthentication();
  const { roomChannels } = useChannels();
  const { isInternetConnectionOffline } = useInternetConnection();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isFlightRoomInfoModalOpen, setIsFlightRoomInfoModalOpen] = useState(false);

  const isChannelsPath = useMemo(() => !!matchPath({ path: '/dashboard/flightrooms/:roomId/channel/*' }, pathname), [pathname]);
  const isPreviewMode = hasOpenTasks(selectedFlightRoom?.tasks);

  const roomId = useMemo(() => {
    const channelMatch = matchPath({ path: '/dashboard/flightrooms/:roomId/channel/:channelId?' }, pathname);
    if (channelMatch) {
      return channelMatch.params.roomId ?? null;
    }
    return matchPath({ path: '/dashboard/flightrooms/:roomId?/:channelId?' }, pathname)?.params.roomId ?? null;
  }, [pathname]);

  const previousSelectedFlightRoom = useRef(selectedFlightRoom);
  useEffect(() => {
    const previousId = previousSelectedFlightRoom.current?.id;
    if (previousId && !selectedFlightRoom && roomId === previousId) {
      navigate('/dashboard/flightrooms');
    }
    previousSelectedFlightRoom.current = selectedFlightRoom;
  }, [navigate, roomId, selectedFlightRoom]);

  useEffect(() => {
    if (roomId) {
      openFlightRoom(roomId);
    } else {
      closeFlightRoom();
    }
  }, [closeFlightRoom, openFlightRoom, roomId]);

  const showOfflineText = !roomChannels?.mainChannel?.users && isInternetConnectionOffline;

  return (
    <section className="chat-container">
      {selectedFlightRoom && user ? (
        <>
          <header>
            <ChatHeader isPreviewMode={isPreviewMode} onClickInfo={() => setIsFlightRoomInfoModalOpen(prev => !prev)} />
          </header>
          {!isPreviewMode ? <ChannelTabs /> : null}
          {roomChannels?.mainChannel && !isPreviewMode ? (
            <FlightRoomInfoSideModal
              channel={roomChannels.mainChannel}
              flightNumber={selectedFlightRoom.flightNumber}
              isVisible={isFlightRoomInfoModalOpen}
              onClose={() => setIsFlightRoomInfoModalOpen(false)}
              roomId={selectedFlightRoom.id}
              showOfflineText={showOfflineText}
            />
          ) : null}
        </>
      ) : null}

      <div className={classNames('chat-content-container', { 'split-view': isChannelsPath && !isPreviewMode })}>
        {isChannelsPath && !isPreviewMode ? (
          <div className="sidebar-container">
            <ChannelSidebar />
          </div>
        ) : null}
        <div className={classNames('outlet-container', { 'split-view': isChannelsPath && !isPreviewMode })}>
          <Outlet />
        </div>
      </div>
    </section>
  );
};
